import React from 'react';
import { Lock, ArrowLeft, Shield, Database, Eye, Trash2, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="privacy-policy-page">
      <Link to="/">
        <Button variant="ghost" className="mb-6 gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Button>
      </Link>

      <div className="gradient-card rounded-2xl border border-white/20 dark:border-white/10 p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold gradient-text">Privacy Policy</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Last updated: January 2025</p>
          </div>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-600 dark:text-slate-300">
          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">1. Introduction</h2>
            <p>
              Tiara Consultants - AI Product Dev Division ("we," "our," or "us") operates Habit2Goals. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
              We are committed to protecting your privacy and ensuring you have a positive experience using our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-500" />
              2. Information We Collect
            </h2>
            
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mt-4 mb-2">Account Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Email address:</strong> Used for account creation, login, and communication</li>
              <li><strong>Name:</strong> Used to personalize your experience</li>
              <li><strong>Password:</strong> Stored securely using industry-standard encryption (bcrypt)</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mt-4 mb-2">Habit Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Habit names, categories, icons, and colors you create</li>
              <li>Completion logs and dates</li>
              <li>Streak information and statistics</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mt-4 mb-2">Payment Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payment processing is handled securely by Stripe</li>
              <li>We do NOT store your credit card numbers or banking details</li>
              <li>We only retain transaction IDs and subscription status</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mt-4 mb-2">Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Login timestamps</li>
              <li>Feature usage patterns (anonymized)</li>
              <li>Device and browser information for troubleshooting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-500" />
              3. How We Use Your Information
            </h2>
            <p>We use the collected information to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Provide and maintain the Habit2Goals service</li>
              <li>Process your subscription and payments</li>
              <li>Send important service notifications</li>
              <li>Generate your habit statistics and analytics</li>
              <li>Improve our application based on usage patterns</li>
              <li>Respond to your support requests</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-orange-500" />
              4. Information Sharing
            </h2>
            <p>We do NOT sell your personal information. We may share data only in these circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>With your consent:</strong> When you use social sharing features to share your progress</li>
              <li><strong>Service providers:</strong> Stripe for payment processing, cloud hosting providers</li>
              <li><strong>Legal requirements:</strong> If required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              5. Data Security
            </h2>
            <p>We implement industry-standard security measures:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Passwords are hashed using bcrypt encryption</li>
              <li>All data transmission uses HTTPS/TLS encryption</li>
              <li>JWT tokens for secure authentication</li>
              <li>Regular security audits and updates</li>
              <li>Secure cloud infrastructure with access controls</li>
            </ul>
            <p className="mt-3 text-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
              We encourage you to use strong, unique passwords for your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              6. Your Rights and Choices
            </h2>
            <p>You have the following rights regarding your data:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Access:</strong> Request a copy of your data via CSV export</li>
              <li><strong>Correction:</strong> Update your account information at any time</li>
              <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
              <li><strong>Portability:</strong> Export your habit data in CSV format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, contact us at <a href="mailto:support@habit2goals.online" className="text-blue-600 dark:text-blue-400 hover:underline">support@habit2goals.online</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">7. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Active account data is retained as long as your account exists</li>
              <li>After account deletion, data is permanently removed within 30 days</li>
              <li>Payment records may be retained for legal/tax purposes as required by law</li>
              <li>Anonymized usage analytics may be retained indefinitely</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">8. Cookies and Tracking</h2>
            <p>
              Habit2Goals uses minimal cookies and local storage only for essential functionality:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Authentication token:</strong> To keep you logged in</li>
              <li><strong>Theme preference:</strong> To remember your light/dark mode choice</li>
            </ul>
            <p className="mt-2">We do not use third-party tracking cookies or advertising trackers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">9. Children's Privacy</h2>
            <p>
              Habit2Goals is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we discover that a child under 13 has provided 
              us with personal information, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any significant changes 
              by posting the new policy on this page and updating the "Last updated" date. We encourage you to 
              review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-3">11. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:<br /><br />
              <strong>Tiara Consultants - AI Product Dev Division</strong><br />
              Email/Suggestions: <a href="mailto:support@habit2goals.online" className="text-blue-600 dark:text-blue-400 hover:underline">support@habit2goals.online</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
